export const clipboards = new Set<(text: string) => void>();

export default async function copyToClipboard(text: string): Promise<void> {
  clipboards.forEach((clipboard) => {
    clipboard(text);
  });

  // Programmatically copy the specified text to the user's clipboard.
  //
  // - Create a dummy span with the text contents, rendered off viewport.
  // - Use the Selection API to select the contents.
  // - Use `execCommand` to trigger a `copy` event on the selection.
  // - Set the clipboard data directly.
  // - Restore the original selection and remove the dummy node from the DOM.

  try {
    return await navigator.clipboard.writeText(text);
  } catch {
    // Fall back to the document.execCommand('copy') method.
  }

  return new Promise((resolve, reject) => {
    // 1. Create a dummy node with the text and attach it to the document.

    const dummyNode = document.createElement('span');

    Object.assign(dummyNode.style, {
      position: 'absolute',
      width: '32px',
      height: '32px',
      top: '-64px',
      left: '-64px',
      overflow: 'hidden',
    });

    dummyNode.contentEditable = 'true';
    dummyNode.appendChild(document.createTextNode(text));

    document.body.appendChild(dummyNode);

    // 2. Store the current text selection, if there's any.

    const selection = window.getSelection();
    const originalRanges: Range[] = [];

    if (selection && selection.rangeCount > 0) {
      for (let i = 0; i < selection.rangeCount; i++) {
        const currentRange = selection.getRangeAt(i);

        if (currentRange) {
          originalRanges.push(currentRange.cloneRange());
        }
      }
    }

    // 3. Create a text range and select the <span>.

    const range = document.createRange();

    range.selectNodeContents(dummyNode);

    selection?.removeAllRanges();
    selection?.addRange(range);

    const handleCopy = (evt: ClipboardEvent) => {
      const {clipboardData} = evt;

      if (!clipboardData) {
        reject(new Error('Could not copy: no clipboardData'));
      }

      try {
        clipboardData?.setData('text/plain', text);
      } catch {
        reject(new Error('Could not copy: failed at setData'));
      }

      evt.preventDefault();

      // 5. Remove the <span> node used to copy.

      document.removeEventListener('copy', handleCopy);
      document.body.removeChild(dummyNode);

      // 6. Restore the old text selection, if there was any.

      if (selection && originalRanges.length > 0) {
        selection.removeAllRanges();

        originalRanges.forEach((originalRange) => {
          selection.addRange(originalRange);
        });
      }

      resolve();
    };

    // 4. Execute a copy command.

    document.addEventListener('copy', handleCopy);
    document.execCommand('copy');
  });
}
